import { useEffect, useRef, useState } from 'react';

/**
 * The pixel width a chart has to draw into.
 *
 * A chart cannot lay out from CSS alone: tick spacing, label density and bar
 * width are arithmetic, and the arithmetic needs a number. `ResizeObserver` is
 * a platform API rather than a dependency, so this stays inside the kit's
 * no-libraries rule.
 *
 * Height is *not* measured. It comes from a prop with a CSS-driven default,
 * because measuring both invites the classic loop: the chart sizes itself to
 * its container, the container sizes itself to its content, and neither ever
 * settles.
 *
 * The initial `0` matters. Rendering a chart at a guessed width and then
 * snapping to the real one is a visible jump on every page load; at zero the
 * frame renders its skeleton and swaps in the drawing once, when it knows.
 */
export function useChartWidth<T extends HTMLElement = HTMLDivElement>(): {
  ref: React.RefObject<T | null>;
  width: number;
} {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      // `contentRect` rather than `borderBoxSize`: the SVG is drawn inside the
      // padding box, so the border and padding are not ours to fill.
      const next = Math.round(entry.contentRect.width);

      // Rounded, and only committed when it actually changed. A sub-pixel
      // wobble from a scrollbar appearing would otherwise re-render every chart
      // on the page on every frame of a resize.
      setWidth((current) => (current === next ? current : next));
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
}
